/*
 * =====================================================================================
 * DISCLAIMER:
 * =====================================================================================
 * This localization file is a community contribution and is not officially maintained
 * by Clerk. It has been provided by the community and may not be fully aligned
 * with the current or future states of the main application. Clerk does not guarantee
 * the accuracy, completeness, or timeliness of the translations in this file.
 * Use of this file is at your own risk and discretion.
 * =====================================================================================
 */

import type { LocalizationResource } from '@clerk/types';

export const skSK: LocalizationResource = {
  locale: 'sk-SK',
  apiKeys: {
    action__add: undefined,
    action__search: undefined,
    createdAndExpirationStatus__expiresOn: undefined,
    createdAndExpirationStatus__never: undefined,
    detailsTitle__emptyRow: undefined,
    formButtonPrimary__add: undefined,
    formFieldCaption__expiration__expiresOn: undefined,
    formFieldCaption__expiration__never: undefined,
    formFieldOption__expiration__180d: undefined,
    formFieldOption__expiration__1d: undefined,
    formFieldOption__expiration__1y: undefined,
    formFieldOption__expiration__30d: undefined,
    formFieldOption__expiration__60d: undefined,
    formFieldOption__expiration__7d: undefined,
    formFieldOption__expiration__90d: undefined,
    formFieldOption__expiration__never: undefined,
    formHint: undefined,
    formTitle: undefined,
    lastUsed__days: undefined,
    lastUsed__hours: undefined,
    lastUsed__minutes: undefined,
    lastUsed__months: undefined,
    lastUsed__seconds: undefined,
    lastUsed__years: undefined,
    menuAction__revoke: undefined,
    revokeConfirmation: {
      confirmationText: undefined,
      formButtonPrimary__revoke: undefined,
      formHint: undefined,
      formTitle: undefined,
    },
  },
  backButton: 'Späť',
  badge__activePlan: undefined,
  badge__canceledEndsAt: undefined,
  badge__currentPlan: 'Aktuálny balík',
  badge__default: 'Predvolené',
  badge__endsAt: undefined,
  badge__expired: undefined,
  badge__freeTrial: undefined,
  badge__otherImpersonatorDevice: 'Iné zariadenie zástupcu',
  badge__pastDueAt: undefined,
  badge__pastDuePlan: undefined,
  badge__primary: 'Hlavný',
  badge__renewsAt: undefined,
  badge__requiresAction: 'Vyžaduje akciu',
  badge__startsAt: undefined,
  badge__thisDevice: 'Toto zariadenie',
  badge__trialEndsAt: undefined,
  badge__unverified: 'Nepotvrdené',
  badge__upcomingPlan: undefined,
  badge__userDevice: 'Zariadenie používateľa',
  badge__you: 'Vy',
  commerce: {
    addPaymentMethod: undefined,
    alwaysFree: undefined,
    annually: undefined,
    availableFeatures: undefined,
    billedAnnually: 'Ročne',
    billedMonthlyOnly: undefined,
    cancelFreeTrial: undefined,
    cancelFreeTrialAccessUntil: undefined,
    cancelFreeTrialTitle: undefined,
    cancelSubscription: undefined,
    cancelSubscriptionAccessUntil: undefined,
    cancelSubscriptionNoCharge: undefined,
    cancelSubscriptionTitle: undefined,
    cannotSubscribeMonthly: undefined,
    cannotSubscribeUnrecoverable: undefined,
    checkout: {
      description__paymentSuccessful: undefined,
      description__subscriptionSuccessful: undefined,
      downgradeNotice: undefined,
      emailForm: {
        subtitle: undefined,
        title: undefined,
      },
      lineItems: {
        title__freeTrialEndsAt: undefined,
        title__paymentMethod: undefined,
        title__statementId: undefined,
        title__subscriptionBegins: undefined,
        title__totalPaid: undefined,
      },
      pastDueNotice: undefined,
      perMonth: undefined,
      title: undefined,
      title__paymentSuccessful: undefined,
      title__subscriptionSuccessful: undefined,
      title__trialSuccess: undefined,
      totalDueAfterTrial: undefined,
    },
    credit: undefined,
    creditRemainder: undefined,
    defaultFreePlanActive: undefined,
    free: 'Bezplatne',
    getStarted: 'Začať',
    keepFreeTrial: undefined,
    keepSubscription: undefined,
    manage: undefined,
    manageSubscription: 'Spravovať členstvo',
    month: 'Mesiac',
    monthly: undefined,
    pastDue: undefined,
    pay: undefined,
    paymentMethods: undefined,
    paymentSource: {
      applePayDescription: {
        annual: undefined,
        monthly: undefined,
      },
      dev: {
        anyNumbers: undefined,
        cardNumber: undefined,
        cvcZip: undefined,
        developmentMode: undefined,
        expirationDate: undefined,
        testCardInfo: undefined,
      },
    },
    popular: undefined,
    pricingTable: {
      billingCycle: undefined,
      included: undefined,
    },
    reSubscribe: undefined,
    seeAllFeatures: undefined,
    startFreeTrial: undefined,
    startFreeTrial__days: undefined,
    subscribe: undefined,
    subscriptionDetails: {
      beginsOn: undefined,
      currentBillingCycle: undefined,
      endsOn: undefined,
      firstPaymentAmount: undefined,
      firstPaymentOn: undefined,
      nextPaymentAmount: undefined,
      nextPaymentOn: undefined,
      pastDueAt: undefined,
      renewsAt: undefined,
      subscribedOn: undefined,
      title: undefined,
      trialEndsOn: undefined,
      trialStartedOn: undefined,
    },
    subtotal: undefined,
    switchPlan: 'Zmeniť balík',
    switchToAnnual: undefined,
    switchToAnnualWithAnnualPrice: undefined,
    switchToMonthly: undefined,
    switchToMonthlyWithPrice: undefined,
    totalDue: undefined,
    totalDueToday: undefined,
    viewFeatures: undefined,
    viewPayment: undefined,
    year: undefined,
  },
  createOrganization: {
    formButtonSubmit: 'Vytvoriť organizáciu',
    invitePage: {
      formButtonReset: 'Preskočiť',
    },
    title: 'Vytvoriť organizáciu',
  },
  dates: {
    lastDay: "Včera o {{ date | timeString('sk-SK') }}",
    next6Days: "Příští {{ date | weekday('sk-SK','long') }} o {{ date | timeString('sk-SK') }}",
    nextDay: "Zajtra o {{ date | timeString('sk-SK') }}",
    numeric: "{{ date | numeric('sk-SK') }}",
    previous6Days: "Minulý {{ date | weekday('sk-SK','long') }} o {{ date | timeString('sk-SK') }}",
    sameDay: "Dnes o {{ date | timeString('sk-SK') }}",
  },
  dividerText: 'alebo',
  footerActionLink__alternativePhoneCodeProvider: undefined,
  footerActionLink__useAnotherMethod: 'Použiť inú metódu',
  footerPageLink__help: 'Pomoc',
  footerPageLink__privacy: 'Ochrana súkromia',
  footerPageLink__terms: 'Podmienky',
  formButtonPrimary: 'Pokračovať',
  formButtonPrimary__verify: 'Overiť',
  formFieldAction__forgotPassword: 'Zabudli ste heslo?',
  formFieldError__matchingPasswords: 'Heslá sa musia zhodovať.',
  formFieldError__notMatchingPasswords: 'Heslá sa nezhodujú.',
  formFieldError__verificationLinkExpired: 'Overovací odkaz vypršal. Vyžiadajte si nový odkaz.',
  formFieldHintText__optional: 'Voliteľné',
  formFieldHintText__slug: 'Slug je čitateľné ID, ktoré musí byť jedinečné. Často sa používa v URL adresách.',
  formFieldInputPlaceholder__apiKeyDescription: undefined,
  formFieldInputPlaceholder__apiKeyExpirationDate: undefined,
  formFieldInputPlaceholder__apiKeyName: undefined,
  formFieldInputPlaceholder__backupCode: 'Zadajte záložný kód',
  formFieldInputPlaceholder__confirmDeletionUserAccount: 'Zmazať účet',
  formFieldInputPlaceholder__emailAddress: 'Zadajte svoj e-mail',
  formFieldInputPlaceholder__emailAddress_username: 'Zadajte e-mail alebo používateľské meno',
  formFieldInputPlaceholder__emailAddresses:
    'Zadajte alebo vložte jednu alebo viac emailových adries oddelených medzerou alebo čiarkou',
  formFieldInputPlaceholder__firstName: 'Krstné meno',
  formFieldInputPlaceholder__lastName: 'Priezvisko',
  formFieldInputPlaceholder__organizationDomain: 'priklad.sk',
  formFieldInputPlaceholder__organizationDomainEmailAddress: 'vy@priklad.sk',
  formFieldInputPlaceholder__organizationName: 'Názov organizácie',
  formFieldInputPlaceholder__organizationSlug: 'moja-organizacia',
  formFieldInputPlaceholder__password: 'Zadajte heslo',
  formFieldInputPlaceholder__phoneNumber: 'Zadajte telefónne číslo',
  formFieldInputPlaceholder__username: 'Zadajte užívateľské meno',
  formFieldLabel__apiKeyDescription: undefined,
  formFieldLabel__apiKeyExpiration: undefined,
  formFieldLabel__apiKeyName: undefined,
  formFieldLabel__automaticInvitations: 'Povoliť automatické pozvánky pre túto doménu',
  formFieldLabel__backupCode: 'Záložný kód',
  formFieldLabel__confirmDeletion: 'Potvrdenie',
  formFieldLabel__confirmPassword: 'Potvrdiť heslo',
  formFieldLabel__currentPassword: 'Súčasné heslo',
  formFieldLabel__emailAddress: 'Emailová adresa',
  formFieldLabel__emailAddress_username: 'Emailová adresa alebo užívateľské meno',
  formFieldLabel__emailAddresses: 'Emailové adresy',
  formFieldLabel__firstName: 'Meno',
  formFieldLabel__lastName: 'Priezvisko',
  formFieldLabel__newPassword: 'Nové heslo',
  formFieldLabel__organizationDomain: 'Doména',
  formFieldLabel__organizationDomainDeletePending: 'Zmazať čakajúce pozvánky a návrhy',
  formFieldLabel__organizationDomainEmailAddress: 'Overovacia e-mailová adresa',
  formFieldLabel__organizationDomainEmailAddressDescription:
    'Zadajte e-mailovú adresu pod touto doménou na prijatie kódu a overenie tejto domény.',
  formFieldLabel__organizationName: 'Názov organizácie',
  formFieldLabel__organizationSlug: 'URL adresa',
  formFieldLabel__passkeyName: 'Názov Passkey',
  formFieldLabel__password: 'Heslo',
  formFieldLabel__phoneNumber: 'Telefónne číslo',
  formFieldLabel__role: 'Rola',
  formFieldLabel__signOutOfOtherSessions: 'Odhlásiť sa zo všetkých ostatných zariadení',
  formFieldLabel__username: 'Užívateľské meno',
  impersonationFab: {
    action__signOut: 'Odhlásiť sa',
    title: 'Prihlásený(á) ako {{identifier}}',
  },
  maintenanceMode: undefined,
  membershipRole__admin: 'Správca',
  membershipRole__basicMember: 'Člen',
  membershipRole__guestMember: 'Host',
  organizationList: {
    action__createOrganization: 'Vytvoriť organizáciu',
    action__invitationAccept: 'Pripojiť sa',
    action__suggestionsAccept: 'Požiadať o pripojenie',
    createOrganization: 'Vytvoriť organizáciu',
    invitationAcceptedLabel: 'Pripojené',
    subtitle: 'na pokračovanie do {{applicationName}}',
    suggestionsAcceptedLabel: 'Čaká na schválenie',
    title: 'Vyberte účet',
    titleWithoutPersonal: 'Vyberte organizáciu',
  },
  organizationProfile: {
    apiKeysPage: {
      title: 'Kľúče API',
    },
    badge__automaticInvitation: 'Automatické pozvánky',
    badge__automaticSuggestion: 'Automatické návrhy',
    badge__manualInvitation: 'Bez automatickej registrácie',
    badge__unverified: 'Neoverené',
    billingPage: {
      paymentHistorySection: {
        empty: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        tableHeader__status: undefined,
      },
      paymentSourcesSection: {
        actionLabel__default: undefined,
        actionLabel__remove: undefined,
        add: undefined,
        addSubtitle: undefined,
        cancelButton: undefined,
        formButtonPrimary__add: undefined,
        formButtonPrimary__pay: undefined,
        payWithTestCardButton: undefined,
        removeResource: {
          messageLine1: undefined,
          messageLine2: undefined,
          successMessage: undefined,
          title: undefined,
        },
        title: undefined,
      },
      start: {
        headerTitle__payments: undefined,
        headerTitle__plans: undefined,
        headerTitle__statements: undefined,
        headerTitle__subscriptions: undefined,
      },
      statementsSection: {
        empty: undefined,
        itemCaption__paidForPlan: undefined,
        itemCaption__proratedCredit: undefined,
        itemCaption__subscribedAndPaidForPlan: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
        totalPaid: undefined,
      },
      subscriptionsListSection: {
        actionLabel__newSubscription: undefined,
        actionLabel__switchPlan: undefined,
        tableHeader__edit: undefined,
        tableHeader__plan: undefined,
        tableHeader__startDate: undefined,
        title: undefined,
      },
      subscriptionsSection: {
        actionLabel__default: undefined,
      },
      switchPlansSection: {
        title: undefined,
      },
      title: undefined,
    },
    createDomainPage: {
      subtitle:
        'Pridajte doménu na overenie. Používatelia s e-mailovými adresami v tejto doméne sa môžu automaticky pripojiť k organizácii alebo požiadať o pripojenie.',
      title: 'Pridať doménu',
    },
    invitePage: {
      detailsTitle__inviteFailed: 'Pozvánky sa nepodarilo odoslať. Opravte nasledujúce a skúste to znovu:',
      formButtonPrimary__continue: 'Odoslať pozvánky',
      selectDropdown__role: 'Vyberte rolu',
      subtitle: 'Pozvať nových členov do tejto organizácie',
      successMessage: 'Pozvánky boli úspešne odoslané.',
      title: 'Pozvať členov',
    },
    membersPage: {
      action__invite: 'Pozvať',
      action__search: 'Hľadať',
      activeMembersTab: {
        menuAction__remove: 'Odstrániť člena',
        tableHeader__actions: 'Akcie',
        tableHeader__joined: 'Pripojil sa',
        tableHeader__role: 'Rola',
        tableHeader__user: 'Užívateľ',
      },
      detailsTitle__emptyRow: 'Žiadni členovia na zobrazenie',
      invitationsTab: {
        autoInvitations: {
          headerSubtitle:
            'Pozvite používateľov prepojením e-mailovej domény s vašou organizáciou. Každý, kto sa zaregistruje s e-mailovou doménou, ktorá sa zhoduje, sa môže k organizácii pripojiť kedykoľvek.',
          headerTitle: 'Automatické pozvánky',
          primaryButton: 'Spravovať overené domény',
        },
        table__emptyRow: 'Žiadne pozvánky na zobrazenie',
      },
      invitedMembersTab: {
        menuAction__revoke: 'Zrušiť pozvanie',
        tableHeader__invited: 'Pozvaní',
      },
      requestsTab: {
        autoSuggestions: {
          headerSubtitle:
            'Používatelia, ktorí sa zaregistrujú s e-mailovou doménou, ktorá sa zhoduje, uvidia návrh na požiadanie o pripojenie do vašej organizácie.',
          headerTitle: 'Automatické návrhy',
          primaryButton: 'Spravovať overené domény',
        },
        menuAction__approve: 'Schváliť',
        menuAction__reject: 'Odmietnuť',
        tableHeader__requested: 'Požiadali o prístup',
        table__emptyRow: 'Žiadne požiadavky na zobrazenie',
      },
      start: {
        headerTitle__invitations: 'Pozvánky',
        headerTitle__members: 'Členovia',
        headerTitle__requests: 'Požiadavky',
      },
    },
    navbar: {
      apiKeys: undefined,
      billing: undefined,
      description: 'Spravujte svoju organizáciu.',
      general: 'Všeobecné',
      members: 'Členovia',
      title: 'Organizácia',
    },
    plansPage: {
      alerts: {
        noPermissionsToManageBilling: undefined,
      },
      title: undefined,
    },
    profilePage: {
      dangerSection: {
        deleteOrganization: {
          actionDescription: 'Pre pokračovanie zadajte "{{organizationName}}" nižšie.',
          messageLine1: 'Ste si istí, že chcete odstrániť túto organizáciu?',
          messageLine2: 'Tento krok je trvalý a nevratný.',
          successMessage: 'Organizácia bola odstránená.',
          title: 'Odstrániť organizáciu',
        },
        leaveOrganization: {
          actionDescription: 'Pre pokračovanie zadajte "{{organizationName}}" nižšie.',
          messageLine1:
            'Naozaj chcete opustiť túto organizáciu? Stratíte prístup k tejto organizácii a jej aplikáciám.',
          messageLine2: 'Táto akcia je trvalá a nezvratná.',
          successMessage: 'Opustili ste organizáciu.',
          title: 'Opustiť organizáciu',
        },
        title: 'Upozornenie',
      },
      domainSection: {
        menuAction__manage: 'Spravovať',
        menuAction__remove: 'Odstrániť',
        menuAction__verify: 'Overiť',
        primaryButton: 'Pridať doménu',
        subtitle:
          'Umožnite používateľom pripojiť sa k organizácii automaticky alebo požiadať o pripojenie na základe overenej e-mailovej domény.',
        title: 'Overené domény',
      },
      successMessage: 'Organizácia bola aktualizovaná.',
      title: 'Profil organizácie',
    },
    removeDomainPage: {
      messageLine1: 'E-mailová doména {{domain}} bude odstránená.',
      messageLine2: 'Používatelia už nebudú môcť automaticky pripojiť organizáciu po tomto kroku.',
      successMessage: '{{domain}} bola odstránená.',
      title: 'Odstrániť doménu',
    },
    start: {
      headerTitle__general: 'Všeobecné',
      headerTitle__members: 'Členovia',
      profileSection: {
        primaryButton: 'Aktualizovať profil',
        title: 'Profil organizácie',
        uploadAction__title: 'Logo',
      },
    },
    verifiedDomainPage: {
      dangerTab: {
        calloutInfoLabel: 'Odstránenie tejto domény ovplyvní pozvaných používateľov.',
        removeDomainActionLabel__remove: 'Odstrániť doménu',
        removeDomainSubtitle: 'Odstrániť túto doménu z overených domén',
        removeDomainTitle: 'Odstrániť doménu',
      },
      enrollmentTab: {
        automaticInvitationOption__description:
          'Používatelia sú automaticky pozývaní na pripojenie sa k organizácii, keď sa zaregistrujú, a môžu sa pripojiť kedykoľvek.',
        automaticInvitationOption__label: 'Automatické pozvánky',
        automaticSuggestionOption__description:
          'Používatelia dostanú návrh na požiadanie o pripojenie, ale musia byť schválení administrátorom predtým, ako sa môžu pripojiť k organizácii.',
        automaticSuggestionOption__label: 'Automatické návrhy',
        calloutInfoLabel: 'Zmena režimu zápisu ovplyvní iba nových používateľov.',
        calloutInvitationCountLabel: 'Čakajúce pozvánky zaslané používateľom: {{count}}',
        calloutSuggestionCountLabel: 'Čakajúce návrhy zaslané používateľom: {{count}}',
        manualInvitationOption__description: 'Používatelia môžu byť pozvaní iba manuálne do organizácie.',
        manualInvitationOption__label: 'Žiadny automatický zápis',
        subtitle: 'Vyberte, ako môžu používatelia z tejto domény vstúpiť do organizácie.',
      },
      start: {
        headerTitle__danger: 'Nebezpečie',
        headerTitle__enrollment: 'Možnosti zápisu',
      },
      subtitle: 'Doména {{domain}} bola teraz overená. Pokračujte výberom režimu zápisu.',
      title: 'Aktualizovať {{domain}}',
    },
    verifyDomainPage: {
      formSubtitle: 'Zadajte overovací kód zaslaný na vašu e-mailovú adresu',
      formTitle: 'Overovací kód',
      resendButton: 'Nedostali ste kód? Znova odoslať',
      subtitle: 'Doména {{domainName}} musí byť overená cez e-mail.',
      subtitleVerificationCodeScreen:
        'Overovací kód bol zaslaný na {{emailAddress}}. Pre pokračovanie ho zadajte nižšie.',
      title: 'Overiť doménu',
    },
  },
  organizationSwitcher: {
    action__createOrganization: 'Vytvoriť organizáciu',
    action__invitationAccept: 'Pripojiť sa',
    action__manageOrganization: 'Spravovať organizáciu',
    action__suggestionsAccept: 'Požiadať o pripojenie',
    notSelected: 'Nie je vybraná žiadna organizácia',
    personalWorkspace: 'Osobný pracovný priestor',
    suggestionsAcceptedLabel: 'Čaká na schválenie',
  },
  paginationButton__next: 'Ďalšie',
  paginationButton__previous: 'Predchádzajúce',
  paginationRowText__displaying: 'Zobrazuje sa',
  paginationRowText__of: 'z',
  reverification: {
    alternativeMethods: {
      actionLink: 'Získať pomoc',
      actionText: 'Nemáte prístup k žiadnej z týchto metód?',
      blockButton__backupCode: 'Použiť záložný kód',
      blockButton__emailCode: 'Odoslať overovací kód na email {{identifier}}',
      blockButton__passkey: 'Použiť Passkey',
      blockButton__password: 'Pokračovať pomocou hesla',
      blockButton__phoneCode: 'Poslať SMS kód na telefónne číslo {{identifier}}',
      blockButton__totp: 'Použiť autentifikačnú aplikáciu',
      getHelp: {
        blockButton__emailSupport: 'Podpora cez email',
        content:
          'Ak máte problémy s overením svojho účtu, kontaktujte nás emailom a pokúsime sa vám čo najskôr obnoviť prístup.',
        title: 'Získať pomoc',
      },
      subtitle: 'Máte problémy? Na overenie môžete použiť jednu z týchto metód.',
      title: 'Použiť inú metódu',
    },
    backupCodeMfa: {
      subtitle: 'Zadajte záložný kód, ktorý ste dostali pri nastavovaní dvojfaktorového overenia.',
      title: 'Zadajte záložný kód',
    },
    emailCode: {
      formTitle: 'Overovací kód',
      resendButton: 'Nedostali ste kód? Znova odoslať',
      subtitle: 'Pre pokračovanie vložte kód odoslaný na váš email',
      title: 'Vyžaduje sa overenie',
    },
    noAvailableMethods: {
      message: 'Nemožno pokračovať v overení. Nie je nastavená žiadna dostupná autentifikačná metóda.',
      subtitle: 'Došlo k chybe',
      title: 'Nepodarilo sa overiť váš účet',
    },
    passkey: {
      blockButton__passkey: 'Použiť Passkey',
      subtitle:
        'Použitie passkey potvrdí vašu identitu. Vaše zariadenie vás môže vyzvať na potvrdenie odtlačkom, tvárou alebo kódom.',
      title: 'Použiť Passkey',
    },
    password: {
      actionLink: 'Použiť inú metódu',
      subtitle: 'Pre pokračovanie vložte svoje aktuálne heslo',
      title: 'Vyžaduje sa overenie',
    },
    phoneCode: {
      formTitle: 'Overovací kód',
      resendButton: 'Nedostali ste kód? Znova odoslať',
      subtitle: 'Pre pokračovanie vložte overovací kód odoslaný na vaše telefónne číslo',
      title: 'Vyžaduje sa overenie',
    },
    phoneCodeMfa: {
      formTitle: 'Overovací kód',
      resendButton: 'Nedostali ste kód? Znova odoslať',
      subtitle: 'Pre pokračovanie vložte overovací kód odoslaný na vaše telefónne číslo',
      title: 'Vyžaduje sa overenie',
    },
    totpMfa: {
      formTitle: 'Overovací kód',
      subtitle: 'Vložte overovací kód z vašej autentifikačnej aplikácie',
      title: 'Vyžaduje sa overenie',
    },
  },
  signIn: {
    accountSwitcher: {
      action__addAccount: 'Pridať účet',
      action__signOutAll: 'Odhlásiť sa zo všetkých účtov',
      subtitle: 'Vyberte účet, ktorý chcete použiť',
      title: 'Vyberte účet',
    },
    alternativeMethods: {
      actionLink: 'Získať pomoc',
      actionText: 'Máte problémy s prihlásením?',
      blockButton__backupCode: 'Použiť záložný kód',
      blockButton__emailCode: 'Odoslať overovací kód na email {{identifier}}',
      blockButton__emailLink: 'Odoslať odkaz na email {{identifier}}',
      blockButton__passkey: 'Prihlásiť sa pomocou Passkey',
      blockButton__password: 'Prihlásiť sa pomocou hesla',
      blockButton__phoneCode: 'Poslať SMS kód na telefónne číslo {{identifier}}',
      blockButton__totp: 'Použiť autentifikačnú aplikáciu',
      getHelp: {
        blockButton__emailSupport: 'Podpora cez email',
        content:
          'Ak máte problémy s prihlásením do svojho účtu, kontaktujte nás emailom a pokúsime sa vám čo najskôr obnoviť prístup.',
        title: 'Získať pomoc',
      },
      subtitle: 'Máte problémy? Na prihlásenie môžete použiť jednu z týchto metód.',
      title: 'Použiť inú metódu',
    },
    alternativePhoneCodeProvider: {
      formTitle: undefined,
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    backupCodeMfa: {
      subtitle: 'pre pokračovanie do {{applicationName}}',
      title: 'Zadajte záložný kód',
    },
    emailCode: {
      formTitle: 'Overovací kód',
      resendButton: 'Znovu poslať kód',
      subtitle: 'pre pokračovanie do {{applicationName}}',
      title: 'Skontrolujte svoj email',
    },
    emailLink: {
      clientMismatch: {
        subtitle: 'Pre pokračovanie otvorte overovací odkaz na zariadení a prehliadači použitom na prihlásenie.',
        title: 'Overovací odkaz nie je platný pre toto zariadenie',
      },
      expired: {
        subtitle: 'Vráťte sa do pôvodného okna pre pokračovanie.',
        title: 'Tento overovací odkaz vypršal',
      },
      failed: {
        subtitle: 'Vráťte sa do pôvodného okna pre pokračovanie.',
        title: 'Tento overovací odkaz je neplatný',
      },
      formSubtitle: 'Použite overovací odkaz zaslaný na váš email',
      formTitle: 'Overovací odkaz',
      loading: {
        subtitle: 'Čoskoro budete presmerovaní',
        title: 'Prihlasujem...',
      },
      resendButton: 'Znovu poslať odkaz',
      subtitle: 'pre pokračovanie do {{applicationName}}',
      title: 'Skontrolujte svoj email',
      unusedTab: {
        title: 'Môžete zatvoriť toto okno',
      },
      verified: {
        subtitle: 'Čoskoro budete presmerovaní',
        title: 'Úspešne prihlásené',
      },
      verifiedSwitchTab: {
        subtitle: 'Vráťte sa do pôvodného okna pre pokračovanie',
        subtitleNewTab: 'Vráťte sa do novootvoreného okna pre pokračovanie',
        titleNewTab: 'Prihlásené v inom okne',
      },
    },
    forgotPassword: {
      formTitle: 'Overovací kód pre obnovenie hesla',
      resendButton: 'Znovu poslať kód',
      subtitle: 'pre obnovenie hesla',
      subtitle_email: 'Najprv zadajte kód zaslaný na váš email',
      subtitle_phone: 'Najprv zadajte kód zaslaný na vaše telefónne číslo',
      title: 'Obnoviť heslo',
    },
    forgotPasswordAlternativeMethods: {
      blockButton__resetPassword: 'Obnoviť heslo',
      label__alternativeMethods: 'Alebo sa prihláste pomocou inej metódy.',
      title: 'Zabudli ste heslo?',
    },
    noAvailableMethods: {
      message: 'Nemožno pokračovať v prihlásení. Nie je k dispozícii žiadna dostupná autentifikačná metóda.',
      subtitle: 'Došlo k chybe',
      title: 'Nie je možné sa prihlásiť',
    },
    passkey: {
      subtitle:
        'Použitie passkey potvrdí vašu identitu. Vaše zariadenie vás môže vyzvať na potvrdenie odtlačkom, tvárou alebo kódom.',
      title: 'Prihlásiť sa pomocou Passkey',
    },
    password: {
      actionLink: 'Použiť inú metódu',
      subtitle: 'pre pokračovanie do {{applicationName}}',
      title: 'Zadajte svoje heslo',
    },
    passwordPwned: {
      title: undefined,
    },
    phoneCode: {
      formTitle: 'Overovací kód',
      resendButton: 'Znova odoslať kód',
      subtitle: 'pre pokračovanie do {{applicationName}}',
      title: 'Skontrolujte váš telefón',
    },
    phoneCodeMfa: {
      formTitle: 'Overovací kód',
      resendButton: 'Znova odoslať kód',
      subtitle: 'Pre pokračovanie zadajte overovací kód zaslaný na vaše telefónne číslo',
      title: 'Skontrolujte váš telefón',
    },
    resetPassword: {
      formButtonPrimary: 'Obnoviť heslo',
      requiredMessage: 'Z bezpečnostných dôvodov je potrebné obnovenie vášho hesla.',
      successMessage: 'Vaše heslo bolo úspešne zmenené. Prihlasujem vás, prosím počkajte okamžite.',
      title: 'Nastaviť nové heslo',
    },
    resetPasswordMfa: {
      detailsLabel: 'Pred obnovením hesla je potrebné overiť vašu totožnosť.',
    },
    start: {
      actionLink: 'Registrovať sa',
      actionLink__join_waitlist: 'Pridať sa na waitlist',
      actionLink__use_email: 'Použiť email',
      actionLink__use_email_username: 'Použiť email alebo užívateľské meno',
      actionLink__use_passkey: 'Použiť Passkey',
      actionLink__use_phone: 'Použiť telefón',
      actionLink__use_username: 'Použiť užívateľské meno',
      actionText: 'Nemáte účet?',
      actionText__join_waitlist: 'Chcete predbežný prístup?',
      alternativePhoneCodeProvider: {
        actionLink: undefined,
        label: undefined,
        subtitle: undefined,
        title: undefined,
      },
      subtitle: 'pre pokračovanie do {{applicationName}}',
      subtitleCombined: undefined,
      title: 'Prihlásiť sa do {{applicationName}}',
      titleCombined: 'Pokračovať do {{applicationName}}',
    },
    totpMfa: {
      formTitle: 'Overovací kód',
      subtitle: 'Pre pokračovanie zadajte overovací kód z vašej autentifikačnej aplikácie',
      title: 'Dvojfaktorové overenie',
    },
  },
  signInEnterPasswordTitle: 'Zadajte svoje heslo',
  signUp: {
    alternativePhoneCodeProvider: {
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    continue: {
      actionLink: 'Prihlásiť sa',
      actionText: 'Máte účet?',
      subtitle: 'Pre pokračovanie prosím doplňte zvyšné informácie.',
      title: 'Vyplňte chýbajúce polia',
    },
    emailCode: {
      formSubtitle: 'Zadajte overovací kód poslaný na vašu emailovú adresu',
      formTitle: 'Overovací kód',
      resendButton: 'Znovu poslať kód',
      subtitle: 'Vložte overovací kód zaslaný na vašu emailovú adresu',
      title: 'Overte svoj email',
    },
    emailLink: {
      clientMismatch: {
        subtitle: 'Pre pokračovanie otvorte overovací odkaz na zariadení a prehliadači použitom na registráciu.',
        title: 'Overovací odkaz nie je platný pre toto zariadenie',
      },
      formSubtitle: 'Použite overovací odkaz poslaný na vašu emailovú adresu',
      formTitle: 'Overovací odkaz',
      loading: {
        title: 'Prebieha registrácia...',
      },
      resendButton: 'Znovu poslať odkaz',
      subtitle: 'pre pokračovanie do {{applicationName}}',
      title: 'Overte svoj email',
      verified: {
        title: 'Úspešne zaregistrované',
      },
      verifiedSwitchTab: {
        subtitle: 'Vráťte sa do novootvoreného okna pre pokračovanie',
        subtitleNewTab: 'Vráťte sa do predchádzajúceho okna pre pokračovanie',
        title: 'Email úspešne overený',
      },
    },
    legalConsent: {
      checkbox: {
        label__onlyPrivacyPolicy: 'Súhlasím s {{ privacyPolicyLink || link("podmienkami o ochrane osobných údajov") }}',
        label__onlyTermsOfService: 'Súhlasím s {{ termsOfServiceLink || link("podmienkami používania") }}',
        label__termsOfServiceAndPrivacyPolicy:
          'Súhlasím s {{ termsOfServiceLink || link("podmienkami používania") }} a {{ privacyPolicyLink || link("podmienkami o ochrane osobných údajov") }}',
      },
      continue: {
        subtitle:
          'Prosím, prečítajte si a súhlaste s našimi podmienkami používania a podmienkami o ochrane osobných údajov.',
        title: 'Súhlas s podmienkami',
      },
    },
    phoneCode: {
      formSubtitle: 'Zadajte overovací kód poslaný na vaše telefónne číslo',
      formTitle: 'Overovací kód',
      resendButton: 'Znovu poslať kód',
      subtitle: 'Pre pokračovanie zadajte overovací kód zaslaný na vaše telefónne číslo',
      title: 'Overte svoj telefón',
    },
    restrictedAccess: {
      actionLink: 'Prihlásiť sa',
      actionText: 'Máte účet?',
      blockButton__emailSupport: 'Podpora cez email',
      blockButton__joinWaitlist: 'Pridať sa na waitlist',
      subtitle:
        'Registrácie aktuálne nie sú povolené. Ak si myslíte, že by ste mali mať prístup, kontaktujte našu podporu.',
      subtitleWaitlist:
        'Registrácie aktuálne nie sú povolené. Ak chete byť informovaní o spustení medzi prvými, pridajte sa na waitlist.',
      title: 'Zablokovaný prístup',
    },
    start: {
      actionLink: 'Prihlásiť sa',
      actionLink__use_email: 'Použiť email',
      actionLink__use_phone: 'Použiť telefón',
      actionText: 'Máte účet?',
      alternativePhoneCodeProvider: {
        actionLink: undefined,
        label: undefined,
        subtitle: undefined,
        title: undefined,
      },
      subtitle: 'Vitajte! Pre pokračovanie prosím vyplňte informácie.',
      subtitleCombined: 'Vitajte! Pre pokračovanie prosím vyplňte informácie.',
      title: 'Vytvorte si účet',
      titleCombined: 'Vytvorte si účet',
    },
  },
  socialButtonsBlockButton: 'Pokračovať s {{provider|titleize}}',
  socialButtonsBlockButtonManyInView: undefined,
  taskChooseOrganization: {
    chooseOrganization: {
      action__createOrganization: undefined,
      action__invitationAccept: undefined,
      action__suggestionsAccept: undefined,
      subtitle: undefined,
      suggestionsAcceptedLabel: undefined,
      title: undefined,
    },
    createOrganization: {
      formButtonReset: undefined,
      formButtonSubmit: undefined,
      formFieldInputPlaceholder__name: undefined,
      formFieldInputPlaceholder__slug: undefined,
      formFieldLabel__name: undefined,
      formFieldLabel__slug: undefined,
      subtitle: undefined,
      title: undefined,
    },
    signOut: {
      actionLink: undefined,
      actionText: undefined,
    },
  },
  unstable__errors: {
    already_a_member_in_organization: undefined,
    captcha_invalid:
      'Registrácia zlyhala z dôvodu neúspešného overenia zabezpečenia. Obnovte stránku a skúste to znova alebo kontaktujte podporu pre ďalšiu pomoc.',
    captcha_unavailable:
      'Registrácia zlyhala z dôvodu neúspešného overenia proti botom. Obnovte stránku a skúste to znova alebo kontaktujte podporu pre ďalšiu pomoc.',
    form_code_incorrect: undefined,
    form_identifier_exists__email_address: 'Táto emailová adresa je už obsadená. Skúste prosím inú.',
    form_identifier_exists__phone_number: 'Toto telefónne číslo je už obsadené. Skúste prosím iné.',
    form_identifier_exists__username: 'Táto použivateľské meno je už obsadené. Skúste prosím iné.',
    form_identifier_not_found: 'Účet s týmto identifikátorom nebol nájdený. Skontrolujte údaje a skúste to znova.',
    form_param_format_invalid: 'Zadaná hodnota nie je v správnom formáte. Skontrolujte a opravte.',
    form_param_format_invalid__email_address: 'Emailová adresa musí byť platná.',
    form_param_format_invalid__phone_number: 'Telefónne číslo musí byť v platnom medzinárodnom formáte.',
    form_param_max_length_exceeded__first_name: 'Krstné meno nesmie byť dlhšie ako 256 znakov.',
    form_param_max_length_exceeded__last_name: 'Priezvisko nesmie byť dlhšie ako 256 znakov.',
    form_param_max_length_exceeded__name: 'Meno nesmie byť dlhšie ako 256 znakov.',
    form_param_nil: 'Pole je povinné a nesmie byť prázdne.',
    form_param_type_invalid: undefined,
    form_param_type_invalid__email_address: undefined,
    form_param_type_invalid__phone_number: undefined,
    form_param_value_invalid: 'Hodnota je neplatná. Skontrolujte a opravte.',
    form_password_incorrect: 'Heslo je nesprávne. Skontrolujte a skúste to znova.',
    form_password_length_too_short: 'Heslo musí mať aspoň 8 znakov.',
    form_password_not_strong_enough: 'Vaše heslo nie je dostatočne silné.',
    form_password_pwned: 'Toto heslo bolo nájdené v rámci úniku dát a nemôže byť použité, prosím zvoľte iné heslo.',
    form_password_pwned__sign_in:
      'Toto heslo bolo nájdené v rámci úniku dát a nemôže byť použité, prosím zvoľte iné heslo.',
    form_password_size_in_bytes_exceeded:
      'Vaše heslo prekročilo maximálny povolený počet bytov, prosím skráťte ho alebo odstráňte niektoré špeciálne znaky.',
    form_password_validation_failed: 'Nesprávne heslo',
    form_username_invalid_character:
      'Používateľské meno môže obsahovať len písmená, číslice, pomlčky a podčiarkovníky.',
    form_username_invalid_length:
      'Používateľské meno musí mať aspoň {{min_length}} znaky a maximálne {{max_length}} znakov.',
    identification_deletion_failed: 'Nemôžete odstrániť váš posledný identifikátor.',
    not_allowed_access: 'Nemáte povolený prístup k tejto stránke. Ak si myslíte, že je to chyba, kontaktujte podporu.',
    organization_domain_blocked: undefined,
    organization_domain_common: undefined,
    organization_domain_exists_for_enterprise_connection: undefined,
    organization_membership_quota_exceeded: undefined,
    organization_minimum_permissions_needed: undefined,
    organization_not_found_or_unauthorized: undefined,
    organization_not_found_or_unauthorized_with_create_organization_disabled: undefined,
    passkey_already_exists: 'Na tomto zariadení už existuje passkey pre tento účet.',
    passkey_not_supported: 'Váš prehliadač alebo zariadenie nepodporuje passkey.',
    passkey_pa_not_supported: 'Registrácia vyžaduje autentifikátor ale toto zariadenie ho nepodporuje.',
    passkey_registration_cancelled: 'Registrácia passkey bola zrušená alebo vypršala.',
    passkey_retrieval_cancelled: 'Overenie passkey bolo zrušené alebo vypršalo.',
    passwordComplexity: {
      maximumLength: 'menej ako {{length}} znakov',
      minimumLength: '{{length}} alebo viac znakov',
      requireLowercase: 'malé písmeno',
      requireNumbers: 'číslicu',
      requireSpecialCharacter: 'špeciálny znak',
      requireUppercase: 'veľké písmeno',
      sentencePrefix: 'Vaše heslo musí obsahovať',
    },
    phone_number_exists: 'Toto telefónne číslo je už obsadené. Skúste prosím iné.',
    session_exists: 'Jste už přihlášen.',
    web3_missing_identifier: 'Rozšírenie Web3 Peňaženky nebolo nájdené. Je potrebné ho nainštalovať.',
    zxcvbn: {
      couldBeStronger: 'Vaše heslo funguje, ale mohlo by byť silnejšie. Skúste pridať viac znakov.',
      goodPassword: 'Dobrá práca. Toto je vynikajúce heslo.',
      notEnough: 'Vaše heslo není dostatočne silné.',
      suggestions: {
        allUppercase: 'Použite veľké písmena len u niektorých, nie všetkých písmen.',
        anotherWord: 'Pridajte viac slov, ktoré nie sú tak bežné.',
        associatedYears: 'Vyhnite sa rokom, ktoré sú s vami spojené.',
        capitalization: 'Písmená píšte s veľkým počiatočným písmenom a viac ako len prvým písmenom.',
        dates: 'Vyhnite sa dátumom a rokom, ktoré sú s vami spojené.',
        l33t: "Vyhnite sa predvídateľným náhradám písmen, napríklad '@' miesto 'a'.",
        longerKeyboardPattern: 'Použite dlhšie vzory na klávesnici a menite smer písania viackrát.',
        noNeed: 'Môžete vytvárať silné heslá aj bez použitia symbolov, čísel alebo veľkých písmen.',
        pwned: 'Ak používate toto heslo aj niekde inde, mali by ste ho zmeniť.',
        recentYears: 'Vyhnite sa nedávnym rokom.',
        repeated: 'Vyhnite sa opakujúcim sa slovám a znakom.',
        reverseWords: 'Vyhnite sa obráteným pravopisom bežných slov.',
        sequences: 'Vyhnite sa bežným sekvenciám znakov.',
        useWords: 'Použite viac slov, ale vyhnite sa bežným frázam.',
      },
      warnings: {
        common: 'Toto je bežne používané heslo.',
        commonNames: 'Bežné mená a priezviská sú ľahko uhádnuteľné.',
        dates: 'Dátum je ľahko uhádnuteľný.',
        extendedRepeat: 'Opakujúce sa vzory znakov ako "abcabcabc" sú ľahko uhádnuteľné.',
        keyPattern: 'Krátke vzory na klávesnici sú ľahko uhádnuteľné.',
        namesByThemselves: 'Samostatné mená alebo priezviská sú ľahko uhádnuteľné.',
        pwned: 'Vaše heslo bolo odhalené pri úniku údajov na internete.',
        recentYears: 'Nedávne roky sú ľahko uhádnuteľné.',
        sequences: 'Bežné sekvencie znakov ako "abc" sú ľahko uhádnuteľné.',
        similarToCommon: 'Toto je podobné bežne používanému heslu.',
        simpleRepeat: 'Opakujúce sa znaky ako "aaa" sú ľahko uhádnuteľné.',
        straightRow: 'Rady klávesnice sú ľahko uhádnuteľné.',
        topHundred: 'Toto je často používané heslo.',
        topTen: 'Toto je často používané heslo.',
        userInputs: 'Heslo by nemalo obsahovať osobné alebo stránkou súvisiace údaje.',
        wordByItself: 'Samostatné slová sú ľahko uhádnuteľné.',
      },
    },
  },
  userButton: {
    action__addAccount: 'Pridať účet',
    action__manageAccount: 'Spravovať účet',
    action__signOut: 'Odhlásiť sa',
    action__signOutAll: 'Odhlásiť sa zo všetkých účtov',
  },
  userProfile: {
    apiKeysPage: {
      title: 'Kľúče API',
    },
    backupCodePage: {
      actionLabel__copied: 'Skopírované!',
      actionLabel__copy: 'Kopírovať všetko',
      actionLabel__download: 'Stiahnuť .txt',
      actionLabel__print: 'Vytlačiť',
      infoText1: 'Pre tento účet budú povolené záložné kódy.',
      infoText2:
        'Záložné kódy uchovávajte tajne a bezpečne. Môžete vygenerovať nové záložné kódy, ak máte podozrenie, že boli skompromitované.',
      subtitle__codelist: 'Uchovávajte ich bezpečne a tajne.',
      successMessage:
        'Záložné kódy sú teraz povolené. Ak stratíte prístup k vášmu overovaciemu zariadeniu, môžete použiť jeden z týchto kódov na prihlásenie do vášho účtu. Každý kód možno použiť iba raz.',
      successSubtitle:
        'Použite jeden z týchto kódov na prihlásenie do vášho účtu, ak stratíte prístup k vášmu overovaciemu zariadeniu.',
      title: 'Pridať overovanie pomocou záložných kódov',
      title__codelist: 'Záložné kódy',
    },
    billingPage: {
      paymentHistorySection: {
        empty: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        tableHeader__status: undefined,
      },
      paymentSourcesSection: {
        actionLabel__default: undefined,
        actionLabel__remove: undefined,
        add: undefined,
        addSubtitle: undefined,
        cancelButton: undefined,
        formButtonPrimary__add: undefined,
        formButtonPrimary__pay: undefined,
        payWithTestCardButton: undefined,
        removeResource: {
          messageLine1: undefined,
          messageLine2: undefined,
          successMessage: undefined,
          title: undefined,
        },
        title: undefined,
      },
      start: {
        headerTitle__payments: undefined,
        headerTitle__plans: 'Balíky',
        headerTitle__statements: 'Faktúry',
        headerTitle__subscriptions: undefined,
      },
      statementsSection: {
        empty: undefined,
        itemCaption__paidForPlan: undefined,
        itemCaption__proratedCredit: undefined,
        itemCaption__subscribedAndPaidForPlan: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
        totalPaid: undefined,
      },
      subscriptionsListSection: {
        actionLabel__newSubscription: undefined,
        actionLabel__switchPlan: undefined,
        tableHeader__edit: undefined,
        tableHeader__plan: undefined,
        tableHeader__startDate: undefined,
        title: undefined,
      },
      subscriptionsSection: {
        actionLabel__default: undefined,
      },
      switchPlansSection: {
        title: undefined,
      },
      title: 'Faturácia a platby',
    },
    connectedAccountPage: {
      formHint: 'Vyberte poskytovateľa pre pripojenie vášho účtu.',
      formHint__noAccounts: 'Nie sú k dispozícii žiadni dostupní externí poskytovatelia účtov.',
      removeResource: {
        messageLine1: '{{identifier}} bude odobraný z tohto účtu.',
        messageLine2: 'Nebudete už môcť používať tento pripojený účet a akékoľvek závislé funkcie prestanú fungovať.',
        successMessage: '{{connectedAccount}} bol odstránený z vášho účtu.',
        title: 'Odstrániť pripojený účet',
      },
      socialButtonsBlockButton: 'Pripojiť účet {{provider|titleize}}',
      successMessage: 'Poskytovateľ bol pridaný k vášmu účtu.',
      title: 'Pridať pripojený účet',
    },
    deletePage: {
      actionDescription: 'Pre pokračovanie napíšte "Zmazať účet".',
      confirm: 'Zmazať účet',
      messageLine1: 'Ste si istý, že chcete zmazať svoj účet?',
      messageLine2: 'Táto akcia je trvalá a nemôže byť vrátená späť.',
      title: 'Zmazať účet',
    },
    emailAddressPage: {
      emailCode: {
        formHint: 'Na túto e-mailovú adresu bude odoslaný overovací kód.',
        formSubtitle: 'Zadajte overovací kód zaslaný na adresu {{identifier}}',
        formTitle: 'Overovací kód',
        resendButton: 'Znovu odoslať kód',
        successMessage: 'E-mailová adresa {{identifier}} bola pridaná k vášmu účtu.',
      },
      emailLink: {
        formHint: 'Na túto e-mailovú adresu bude odoslaný overovací odkaz.',
        formSubtitle: 'Kliknite na overovací odkaz v e-maile zaslanom na adresu {{identifier}}',
        formTitle: 'Overovací odkaz',
        resendButton: 'Znovu odoslať odkaz',
        successMessage: 'E-mailová adresa {{identifier}} bola pridaná k vášmu účtu.',
      },
      enterpriseSSOLink: {
        formButton: 'Pre prihlásenie kliknite',
        formSubtitle: 'Dokončiť prihlásenie pomocou {{identifier}}',
      },
      formHint: 'Pred pridaním tejto e-mailovej adresy k vášmu účtu je potrebné jej overenie.',
      removeResource: {
        messageLine1: '{{identifier}} bude odstránená z tohto účtu.',
        messageLine2: 'Nebudete sa môcť prihlásiť pomocou tejto e-mailovej adresy.',
        successMessage: '{{emailAddress}} bola odobraná z vášho účtu.',
        title: 'Odstrániť e-mailovú adresu',
      },
      title: 'Pridať e-mailovú adresu',
      verifyTitle: 'Overiť e-mailovú adresu',
    },
    formButtonPrimary__add: 'Pridať',
    formButtonPrimary__continue: 'Pokračovať',
    formButtonPrimary__finish: 'Dokončiť',
    formButtonPrimary__remove: 'Odstrániť',
    formButtonPrimary__save: 'Uložiť',
    formButtonReset: 'Zrušiť',
    mfaPage: {
      formHint: 'Vyberte spôsob pridania.',
      title: 'Pridať dvojfaktorové overenie',
    },
    mfaPhoneCodePage: {
      backButton: 'Použiť existujúce číslo',
      primaryButton__addPhoneNumber: 'Pridať telefónne číslo',
      removeResource: {
        messageLine1: '{{identifier}} už nebude dostávať overovacie kódy pri prihlasovaní.',
        messageLine2: 'Váš účet nemusí byť tak bezpečný. Naozaj chcete pokračovať?',
        successMessage: 'Dvojfaktorové overovanie pomocou SMS kódu bolo odstránené pre {{mfaPhoneCode}}',
        title: 'Odstrániť dvojfaktorové overovanie',
      },
      subtitle__availablePhoneNumbers:
        'Vyberte telefónne číslo pre registráciu dvojfaktorového overovania pomocou SMS kódu.',
      subtitle__unavailablePhoneNumbers:
        'Nie sú k dispozícii žiadne dostupné telefónne čísla pre registráciu dvojfaktorového overovania pomocou SMS kódu.',
      successMessage1:
        'Ako dodatočný krok, pri prihlásení budete musieť zadať overovací kód odoslaný na toto telefónne číslo.',
      successMessage2:
        'Uložte si tieto záložné kódy a uchovajte ich na bezpečnom mieste. Ak stratíte prístup k svojmu autentifikačnému zariadeniu, môžete sa prihlásiť pomocou záložných kódov.',
      successTitle: 'Dvojfaktorové overenie pomocou SMS kódu je teraz povolené',
      title: 'Pridať overovanie pomocou SMS kódu',
    },
    mfaTOTPPage: {
      authenticatorApp: {
        buttonAbleToScan__nonPrimary: 'Namiesto toho naskenujte QR kód',
        buttonUnableToScan__nonPrimary: 'Nemôžete naskenovať QR kód?',
        infoText__ableToScan:
          'Nastavte novú metódu prihlásenia vo vašej aplikácii pre overovanie a naskenujte nasledujúci QR kód, aby ste ju spojili so svojím účtom.',
        infoText__unableToScan:
          'Nastavte novú metódu prihlásenia vo vašej aplikácii pre overovanie a zadajte nižšie uvedený kľúč.',
        inputLabel__unableToScan1:
          'Uistite sa, že je povolené časovo závislé alebo jednorázové heslo a dokončite spojenie vášho účtu.',
        inputLabel__unableToScan2:
          'Alternatívne, ak vaša aplikácia pre overovanie podporuje TOTP URI, môžete tiež skopírovať celý URI.',
      },
      removeResource: {
        messageLine1: 'Pri prihlasovaní už nebudú vyžadované overovacie kódy z tejto aplikácie pre overovanie.',
        messageLine2: 'Váš účet nemusí byť tak bezpečný. Naozaj chcete pokračovať?',
        successMessage: 'Dvojfaktorové overovanie pomocou aplikácie pre overovanie bolo odstránené.',
        title: 'Odstrániť dvojfaktorové overovanie',
      },
      successMessage:
        'Dvojfaktorové overovanie je teraz povolené. Pri prihlásení budete musieť zadať overovací kód z tejto aplikácie pre overovanie ako ďalší krok.',
      title: 'Pridať aplikáciu pre overovanie',
      verifySubtitle: 'Zadajte overovací kód generovaný vašou aplikáciou pre overovanie.',
      verifyTitle: 'Overovací kód',
    },
    mobileButton__menu: 'Menu',
    navbar: {
      account: 'Profil',
      apiKeys: 'Kľúče API',
      billing: 'Fakturácia',
      description: 'Spravujte údaje svojho účtu a zabezpečenie',
      security: 'Bezpečnosť',
      title: 'Účet',
    },
    passkeyScreen: {
      removeResource: {
        messageLine1: '{{name}} bude odstránený z tohto účtu.',
        title: 'Odstrániť Passkey',
      },
      subtitle__rename: 'Zmeňte názov Passkey na niečo, čo vám pomôže si ho zapamätať.',
      title__rename: 'Premenovať Passkey',
    },
    passwordPage: {
      checkboxInfoText__signOutOfOtherSessions:
        'Odporúča sa odhlásiť zo všetkých ostatných zariadení, ktoré mohli používať vaše staré heslo.',
      readonly: 'Vaše heslo momentálne nie je možné upraviť, pretože sa môžete prihlásiť iba cez firemné prihlásenie.',
      successMessage__set: 'Vaše heslo bolo nastavené.',
      successMessage__signOutOfOtherSessions: 'Všetky ostatné zariadenia boli odhlásené.',
      successMessage__update: 'Vaše heslo bolo aktualizované.',
      title__set: 'Nastaviť heslo',
      title__update: 'Zmeniť heslo',
    },
    phoneNumberPage: {
      infoText: 'Na toto telefónne číslo bude odoslaná textová správa obsahujúca overovací odkaz.',
      removeResource: {
        messageLine1: '{{identifier}} bude odobrané z tohto účtu.',
        messageLine2: 'Nebudete sa môcť prihlásiť pomocou tohto telefónneho čísla.',
        successMessage: '{{phoneNumber}} bolo odstránené z vášho účtu.',
        title: 'Odstrániť telefónne číslo',
      },
      successMessage: '{{identifier}} bolo pridané k vášmu účtu.',
      title: 'Pridať telefónne číslo',
      verifySubtitle: 'Zadajte overovací kód odoslaný na {{identifier}}',
      verifyTitle: 'Overte telefónne číslo',
    },
    plansPage: {
      title: undefined,
    },
    profilePage: {
      fileDropAreaHint: 'Nahrajte obrázok vo formátoch JPG, PNG, GIF alebo WEBP s veľkosťou menšou než 10 MB',
      imageFormDestructiveActionSubtitle: 'Odstrániť obrázok',
      imageFormSubtitle: 'Nahrať obrázok',
      imageFormTitle: 'Profilový obrázok',
      readonly: 'Informácie vo vašom profile boli poskytnuté firemným prihlásením a nie je možné ich upraviť.',
      successMessage: 'Váš profil bol aktualizovaný.',
      title: 'Aktualizovať profil',
    },
    start: {
      activeDevicesSection: {
        destructiveAction: 'Odhlásiť sa zo zariadenia',
        title: 'Aktívne zariadenia',
      },
      connectedAccountsSection: {
        actionLabel__connectionFailed: 'Skúsiť znovu',
        actionLabel__reauthorize: 'Autorizovať teraz',
        destructiveActionTitle: 'Odstrániť',
        primaryButton: 'Pripojiť účet',
        subtitle__disconnected: 'Tento účet bol odpojený.',
        subtitle__reauthorize:
          'Požadované oprávnenia boli aktualizované, a preto môžete zaznamenať obmedzenú funkcionalitu. Znovu autorizujte túto aplikáciu, aby ste predišli problémom',
        title: 'Pripojené účty',
      },
      dangerSection: {
        deleteAccountButton: 'Zmazať účet',
        title: 'Zmazanie účtu',
      },
      emailAddressesSection: {
        destructiveAction: 'Odstrániť emailovú adresu',
        detailsAction__nonPrimary: 'Nastaviť ako hlavnú',
        detailsAction__primary: 'Dokončiť overenie',
        detailsAction__unverified: 'Dokončiť overenie',
        primaryButton: 'Pridať emailovú adresu',
        title: 'Emailové adresy',
      },
      enterpriseAccountsSection: {
        title: 'Firemné účty',
      },
      headerTitle__account: 'Účet',
      headerTitle__security: 'Bezpečnosť',
      mfaSection: {
        backupCodes: {
          actionLabel__regenerate: 'Obnoviť kódy',
          headerTitle: 'Záložné kódy',
          subtitle__regenerate:
            'Získajte novú sadu zabezpečených záložných kódov. Predchádzajúce záložné kódy budú zmazané a nebudú použiteľné.',
          title__regenerate: 'Obnoviť záložné kódy',
        },
        phoneCode: {
          actionLabel__setDefault: 'Nastaviť ako predvolené',
          destructiveActionLabel: 'Odstrániť telefónne číslo',
        },
        primaryButton: 'Pridať dvojfaktorovú autentifikáciu',
        title: 'Dvojfaktorová autentifikácia',
        totp: {
          destructiveActionTitle: 'Odstrániť',
          headerTitle: 'Aplikácia Authenticator',
        },
      },
      passkeysSection: {
        menuAction__destructive: 'Odstrániť',
        menuAction__rename: 'Premenovať',
        primaryButton: 'Pridať Passkey',
        title: 'Passkey',
      },
      passwordSection: {
        primaryButton__setPassword: 'Nastaviť heslo',
        primaryButton__updatePassword: 'Zmeniť heslo',
        title: 'Heslo',
      },
      phoneNumbersSection: {
        destructiveAction: 'Odstrániť telefónne číslo',
        detailsAction__nonPrimary: 'Nastaviť ako hlavné',
        detailsAction__primary: 'Dokončiť overenie',
        detailsAction__unverified: 'Dokončiť overenie',
        primaryButton: 'Pridať telefónne číslo',
        title: 'Telefónne čísla',
      },
      profileSection: {
        primaryButton: 'Aktualizovať profil',
        title: 'Profil',
      },
      usernameSection: {
        primaryButton__setUsername: 'Nastaviť užívateľské meno',
        primaryButton__updateUsername: 'Zmeniť užívateľské meno',
        title: 'Užívateľské meno',
      },
      web3WalletsSection: {
        destructiveAction: 'Odstrániť peňaženku',
        detailsAction__nonPrimary: 'Nastaiť ako hlavnú',
        primaryButton: 'Web3 peňaženky',
        title: 'Web3 peňaženky',
      },
    },
    usernamePage: {
      successMessage: 'Vaše užívateľské meno bolo aktualizované.',
      title__set: 'Aktualizovať užívateľské meno',
      title__update: 'Aktualizovať užívateľské meno',
    },
    web3WalletPage: {
      removeResource: {
        messageLine1: '{{identifier}} bude odobraná z tohto účtu.',
        messageLine2: 'Nebudete sa už môcť prihlásiť pomocou tejto web3 peňaženky.',
        successMessage: '{{web3Wallet}} bola odstránená z vášho účtu.',
        title: 'Odstrániť web3 peňaženku',
      },
      subtitle__availableWallets: 'Vyberte web3 peňaženku na pripojenie k vášmu účtu.',
      subtitle__unavailableWallets: 'Nie sú k dispozícii žiadne dostupné web3 peňaženky.',
      successMessage: 'Peňaženka bola pridaná k vášmu účtu.',
      title: 'Pridať web3 peňaženku',
      web3WalletButtonsBlockButton: undefined,
    },
  },
  waitlist: {
    start: {
      actionLink: 'Prihlásiť sa',
      actionText: 'Máte prístup?',
      formButton: 'Pridať sa na waitlist',
      subtitle: 'Zadajte svoj email a my vás budeme informovať, keď bude prístup povolený.',
      title: 'Pridať sa na waitlist',
    },
    success: {
      message: 'Čoskoro budete presmerovaní...',
      subtitle: 'Budeme vás informovať, keď bude prístup povolený.',
      title: 'Ďakujeme, že ste sa pridali na waitlist!',
    },
  },
} as const;
